import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';
import { createUISlice } from './ui';
import type { UISlice } from './ui';
import { createConfigSlice } from './config';
import type { ConfigSlice } from './config';
import { createEntrySlice } from './entries';
import type {
  EntrySlice,
  ICellEntry,
  IEntry,
  IGraphEntry,
  IInternalEntry,
  IMapEntry,
  IMapChildren,
  ISimpleEntry,
  IBooleanEntry,
  ICodeEntry,
} from './entries';
import { createGraphsSlice } from './graphs';
import type {
  GraphsSlice,
  IGraph,
  INode,
  IEdge,
  IEvaluation,
  IValidation,
} from './graphs';
import { createAiSuggestionsSlice } from './ai-suggestions';
import type { AiSuggestionsSlice } from './ai-suggestions';
import { createChatSlice } from './chat';
import type { ChatSlice } from './chat';

enableMapSet();

/**
 * Helper interfaces for the global application store.
 */
export type { ChatIntent, SavedSession } from './chat';
export type {
  Suggestion,
  StoredSuggestion,
  SuggestionFound,
} from './ai-suggestions';
export type {
  IEntry,
  IBooleanEntry,
  ISimpleEntry,
  ICellEntry,
  IGraphEntry,
  IInternalEntry,
  IMapEntry,
  IMapChildren,
  IGraph,
  INode,
  IEdge,
  IEvaluation,
  IValidation,
  ICodeEntry,
};
/**
 * The global application store interface.
 */
export type StoreSlice = {
  ui: UISlice;
} & {
  config: ConfigSlice;
} & {
  entries: EntrySlice;
} & {
  graphs: GraphsSlice;
} & {
  ai: AiSuggestionsSlice;
} & {
  chat: ChatSlice;
};

/**
 * Initialisation of the global application store,
 * which combines multiple slices separated by topic.
 * Slices are merged using namespaces.
 *
 * @returns {StoreSlice} The global application store
 */
export const useStore = create<StoreSlice>()(
  immer((...a) => ({
    ui: createUISlice(...a),
    config: createConfigSlice(...a),
    graphs: createGraphsSlice(...a),
    entries: createEntrySlice(...a),
    ai: createAiSuggestionsSlice(...a),
    chat: createChatSlice(...a),
  })),
);
